/**
 * @file Renders the investment form, picking the schema for the selected
 * profile type and submitting the investment creation or update mutation.
 */
import { useMemo, useEffect, type ReactNode } from "react";
import { Alert } from "antd";

import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";
import type { Operation } from "@api";
import Form, { useForm, useWatch } from "@components/Form";
import { INVESTMENT_PROFILE_TYPE_ENTITY } from "@components/Domain";
import {
  createInvestmentOperation,
  updateMyInvestmentOperation,
} from "@api/services/investments";

import getProfilesMap from "./helpers/getProfilesMap";
import useUpdateSchema from "./helpers/useUpdateSchema";
import setDefaultProfile from "./helpers/setDefaultProfile";
import { useUsTaxWithholdingNotice } from "./UsTaxWithholdingNoticeModal";
import type { ProfileEntity } from "../helpers/types";
import type {
  Form as FormInstance,
  Tier,
  Profile,
  Investment,
  Organization,
  FormIdentity,
} from "./types";

const TITLE_ALERT = "Alert";
const ERROR_UPDATE_FAILED = "Failed to update an investment profile";
const ERROR_CREATE_FAILED = "Failed to create an investment profile";

type SubmitParameters = {
  mutation: {
    projectId?: string;
    isProfileValid?: boolean;
  };
};

// The props that reach the Form component unchanged.
type FormPassThroughProps = {
  formProps?: Record<string, unknown>;
  expectedErrors?: string[];
  hasSubmitButton?: boolean;
  onSubmitFailed?: () => void;
  submitItemProps?: Record<string, unknown>;
  onSubmitConfirm?: (parameters: unknown, action: () => unknown) => unknown;
  submitButtonProps?: Record<string, unknown>;
};

type Props = FormPassThroughProps & {
  form?: FormInstance;
  tiers: Tier[];
  block?: ReactNode;
  isFund: boolean;
  identity?: FormIdentity;
  setBlock?: (block?: ReactNode) => void;
  onFailure?: () => void;
  projectId?: string;
  investment?: Investment;
  submitLabel?: string;
  organization: Organization;
  onAfterSubmit: (investment: Investment) => void;
  createOperation?: Operation;
  updateOperation?: Operation;
  existingProfiles?: Profile[];
  otherInvestments?: Investment[];
  isTypeSelectDisabled?: boolean;
};

/**
 * Renders the investment form for the selected profile type and submits
 * the investment creation or update mutation on confirm.
 */
const InvestmentForm = ({
  form: customForm = undefined,
  identity: customIdentity = undefined,
  onFailure: onCustomFailure = () => undefined,
  block = undefined,
  setBlock = undefined,
  projectId = undefined,
  investment = undefined,
  submitLabel = undefined,
  createOperation = createInvestmentOperation,
  updateOperation = updateMyInvestmentOperation,
  otherInvestments = undefined,
  existingProfiles = undefined,
  isTypeSelectDisabled = false,
  onSubmitConfirm: onSubmitConfirmCustom = undefined,
  tiers,
  isFund,
  organization,
  onAfterSubmit,
  ...otherFormProps
}: Props) => {
  const form = useForm(customForm);

  const {
    notice,
    onIsUsPersonChange,
    onSubmitConfirm,
  } = useUsTaxWithholdingNotice({
    form,
    onSubmitConfirm: onSubmitConfirmCustom,
  });

  const {
    request,
    showErrorMessage,
  } = useAppContext() as AppContext;

  const profilesMap = useMemo(() =>
    getProfilesMap(isFund, existingProfiles, otherInvestments)
  , [
    isFund,
    existingProfiles,
    otherInvestments,
  ]);

  const schema = useUpdateSchema({
    form,
    tiers,
    setBlock,
    investment,
    profilesMap,
    organization,
    customIdentity,
    otherInvestments,
    onIsUsPersonChange,
    isTypeSelectDisabled,
  });

  const profileType = useWatch("profileType", form);

  useEffect(() =>
    setDefaultProfile(form, profileType, profilesMap)
  , [
    form,
    profileType,
    profilesMap,
  ]);

  const isUpdate = !!investment;
  const isCreate = !isUpdate;

  const formProps: FormPassThroughProps & { item?: Investment; operation: Operation } = {
    operation: createOperation,
    ...otherFormProps,
  };

  if (isUpdate) {
    formProps.item = investment;
    formProps.operation = updateOperation;
  }

  /**
   * Shows the create or update failure message and calls the caller's
   * failure callback.
   */
  const onFailure = () => {
    const message = isUpdate
      ? ERROR_UPDATE_FAILED
      : ERROR_CREATE_FAILED;

    showErrorMessage(message);
    onCustomFailure();
  };

  // NOTE: the parameter is optional because TypeScript infers the onSuccess
  //       prop of the JavaScript Form component from its empty default.
  /**
   * Forwards the created or updated investment to the caller's
   * after-submit callback.
   */
  const onSuccess = (updatedInvestment?: Investment) => onAfterSubmit(updatedInvestment);

  /**
   * Adds the project id on create and marks the profile as valid before
   * the mutation submits.
   */
  const onSubmit = (parameters: SubmitParameters) => {
    if (isCreate) {
      parameters.mutation.projectId = projectId;
    }

    parameters.mutation.isProfileValid = true;

    return parameters;
  };

  return (
    <>
      <Form
        form={form}
        schema={schema}
        request={block ? undefined : request}
        onSubmit={onSubmit}
        onSuccess={onSuccess}
        onFailure={onFailure}
        submitLabel={submitLabel}
        onSubmitConfirm={onSubmitConfirm}
        {...formProps}
      />

      {notice}

      {
        block && (
          <Alert
            showIcon
            type="warning"
            message={TITLE_ALERT}
            description={block}
          />
        )
      }
    </>
  );
};

export default InvestmentForm;

type EntityInvestment = {
  profileType?: string;
  profileEntity?: ProfileEntity;
};

/**
 * Flags each entity-profile investment that must provide an EIN or a
 * beneficial-owner EIN, based on the numbers already on its profile.
 */
export const processInvestments = <T extends EntityInvestment>(investments: T[]): T[] => {
  const entityProfileInvestments = investments
    .filter((investment) => investment.profileType === INVESTMENT_PROFILE_TYPE_ENTITY);

  for (const investment of entityProfileInvestments) {
    const { profileEntity } = investment;

    if (profileEntity?.einNumber) {
      // eslint-disable-next-line no-underscore-dangle
      investment.profileEntity._shouldProvideEin = true;
    }

    if (profileEntity?.beneficialOwnerEinNumber) {
      // eslint-disable-next-line no-underscore-dangle
      investment.profileEntity._shouldProvideBeneficialOwnerEin = true;
    }
  }

  return investments;
};
